
import { supabase } from '@/integrations/supabase/client';

export interface UserProfile {
  id: string;
  email: string;
  nickname: string;
  full_name: string;
  age?: number | null;
  gender?: string | null;
  country?: string | null;
  points?: number | null;
  invite_code?: string | null;
  is_verified: boolean;
  is_admin: boolean;
  created_at: string;
}

export interface AdminStats {
  totalUsers: number;
  totalReviews: number;
  totalGuides: number;
  totalRedemptions: number;
  todayNewMembers: number;
  todayNewReviews: number;
  activeMembers: number;
  totalMembers: number;
  weeklyReviews: Array<{ date: string; count: number }>;
  popularCities: Array<{ city: string; count: number }>;
}

export interface Article {
  id: string;
  title: string;
  content: string;
  summary?: string;
  category: string;
  cover_image_url?: string;
  status: string;
  author_id: string;
  created_at: string;
  updated_at: string;
  profiles?: {
    nickname: string;
  } | null;
}

export interface SystemSetting {
  id: string;
  setting_key: string;
  setting_value: string;
  description?: string;
}

// Interface for profile data from Supabase
interface ProfileData {
  id: string;
  nickname: string;
  full_name: string;
  age?: number | null;
  gender?: string | null;
  country?: string | null;
  points?: number | null;
  invite_code?: string | null;
  is_verified?: boolean | null;
  is_admin?: boolean | null;
  created_at?: string | null;
}

// Interface for auth user data from Supabase
interface AuthUserData {
  id: string;
  email?: string;
  created_at?: string;
}

class AdminService {
  async getUsers(): Promise<UserProfile[]> {
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        throw profilesError;
      }

      // Try to get auth users, but don't fail if it doesn't work
      let authUsersArray: AuthUserData[] = [];
      try {
        const { data: authUsers, error: authError } = await supabase.auth.admin.listUsers();
        if (!authError && authUsers) {
          authUsersArray = authUsers.users || [];
        }
      } catch (authError) {
        console.warn('Could not fetch auth users, continuing with profiles only:', authError);
      }

      // Ensure profiles is an array and provide proper typing
      const profilesArray: ProfileData[] = profiles || [];
      
      const users: UserProfile[] = profilesArray.map((profile: ProfileData) => {
        const authUser = authUsersArray.find((user: AuthUserData) => user.id === profile.id);
        return {
          id: profile.id,
          email: authUser?.email || '',
          nickname: profile.nickname,
          full_name: profile.full_name,
          age: profile.age,
          gender: profile.gender,
          country: profile.country,
          points: profile.points,
          invite_code: profile.invite_code,
          is_verified: profile.is_verified || false,
          is_admin: profile.is_admin || false,
          created_at: profile.created_at || new Date().toISOString(),
        };
      });

      return users;
    } catch (error) {
      console.error('Error in getUsers:', error);
      throw error;
    }
  }

  async updateUser(userId: string, updates: Partial<UserProfile>): Promise<void> {
    const { error } = await supabase
      .from('profiles')
      .update({
        nickname: updates.nickname,
        full_name: updates.full_name,
        age: updates.age,
        gender: updates.gender,
        country: updates.country,
        points: updates.points,
        is_verified: updates.is_verified,
        is_admin: updates.is_admin,
      })
      .eq('id', userId);

    if (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async deleteUser(userId: string): Promise<void> {
    // Delete from profiles table first
    const { error: profileError } = await supabase
      .from('profiles')
      .delete()
      .eq('id', userId);

    if (profileError) {
      console.error('Error deleting profile:', profileError);
      throw profileError;
    }

    // Try to delete from auth.users using admin API, but don't fail if it doesn't work
    try {
      const { error: authError } = await supabase.auth.admin.deleteUser(userId);
      if (authError) {
        console.warn('Could not delete auth user:', authError);
      }
    } catch (authError) {
      console.warn('Could not delete auth user:', authError);
    }
  }

  async getStats(): Promise<AdminStats> {
    const [usersResult, reviewsResult, guidesResult, redemptionsResult] = await Promise.all([
      supabase.from('profiles').select('id', { count: 'exact' }),
      supabase.from('reviews').select('id', { count: 'exact' }),
      supabase.from('guides').select('id', { count: 'exact' }),
      supabase.from('reward_redemptions').select('id', { count: 'exact' })
    ]);

    return {
      totalUsers: usersResult.count || 0,
      totalReviews: reviewsResult.count || 0,
      totalGuides: guidesResult.count || 0,
      totalRedemptions: redemptionsResult.count || 0,
      todayNewMembers: 0,
      todayNewReviews: 0,
      activeMembers: 0,
      totalMembers: usersResult.count || 0,
      weeklyReviews: [],
      popularCities: [],
    };
  }

  async getDashboardStats(): Promise<AdminStats> {
    const today = new Date().toISOString().split('T')[0];
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const [
      usersResult,
      reviewsResult,
      guidesResult,
      redemptionsResult,
      todayUsersResult,
      todayReviewsResult,
      weeklyReviewsResult,
      popularCitiesResult
    ] = await Promise.all([
      supabase.from('profiles').select('id', { count: 'exact' }),
      supabase.from('reviews').select('id', { count: 'exact' }),
      supabase.from('guides').select('id', { count: 'exact' }),
      supabase.from('reward_redemptions').select('id', { count: 'exact' }),
      supabase.from('profiles').select('id', { count: 'exact' }).gte('created_at', today),
      supabase.from('reviews').select('id', { count: 'exact' }).gte('created_at', today),
      supabase.from('reviews').select('created_at').gte('created_at', weekAgo),
      supabase.from('reviews').select('region').limit(10)
    ]);

    // Process weekly reviews data
    const weeklyReviews = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const count = weeklyReviewsResult.data?.filter(review => 
        review.created_at.startsWith(date)
      ).length || 0;
      weeklyReviews.push({ date, count });
    }

    // Process popular cities data
    const cityCount: { [key: string]: number } = {};
    popularCitiesResult.data?.forEach(review => {
      if (review.region) {
        cityCount[review.region] = (cityCount[review.region] || 0) + 1;
      }
    });
    const popularCities = Object.entries(cityCount)
      .map(([city, count]) => ({ city, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return {
      totalUsers: usersResult.count || 0,
      totalReviews: reviewsResult.count || 0,
      totalGuides: guidesResult.count || 0,
      totalRedemptions: redemptionsResult.count || 0,
      todayNewMembers: todayUsersResult.count || 0,
      todayNewReviews: todayReviewsResult.count || 0,
      activeMembers: Math.floor((usersResult.count || 0) * 0.3), // Approximate active members
      totalMembers: usersResult.count || 0,
      weeklyReviews,
      popularCities,
    };
  }

  async getReviews(): Promise<any[]> {
    try {
      // First, get all reviews
      const { data: reviews, error: reviewsError } = await supabase
        .from('reviews')
        .select('*')
        .order('created_at', { ascending: false });

      if (reviewsError) {
        console.error('Error fetching reviews:', reviewsError);
        throw reviewsError;
      }

      if (!reviews || reviews.length === 0) {
        return [];
      }

      // Get unique user IDs from reviews
      const userIds = [...new Set(reviews.map(review => review.user_id))];
      
      // Fetch profiles for these users
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, nickname')
        .in('id', userIds);

      if (profilesError) {
        console.warn('Error fetching profiles for reviews:', profilesError);
        // Continue without profile data
        return reviews.map(review => ({
          ...review,
          profiles: null
        }));
      }

      // Create a map of user ID to profile for quick lookup
      const profileMap = new Map();
      profiles?.forEach(profile => {
        profileMap.set(profile.id, profile);
      });

      // Combine reviews with profile data
      const reviewsWithProfiles = reviews.map(review => ({
        ...review,
        profiles: profileMap.get(review.user_id) || null
      }));

      return reviewsWithProfiles;
    } catch (error) {
      console.error('Error in getReviews:', error);
      throw error;
    }
  }

  async updateReview(reviewId: string, updates: any): Promise<void> {
    const { error } = await supabase
      .from('reviews')
      .update({
        rating: updates.rating,
        comment: updates.comment,
        housing_type: updates.housing_type,
        rent: updates.rent,
        deposit_value: updates.deposit_value,
        deposit_type: updates.deposit_type,
        community_name: updates.community_name,
        landlord_name: updates.landlord_name,
        bedrooms: updates.bedrooms,
        living_rooms: updates.living_rooms,
        bathrooms: updates.bathrooms,
        includes_utilities: updates.includes_utilities,
        pet_allowed: updates.pet_allowed,
        immigrant_friendly: updates.immigrant_friendly,
        parking_information: updates.parking_information,
        utilities_included: updates.utilities_included,
        required_documents: updates.required_documents,
        updated_at: new Date().toISOString(),
      })
      .eq('id', reviewId);

    if (error) {
      console.error('Error updating review:', error);
      throw error;
    }
  }

  async deleteReview(reviewId: string): Promise<void> {
    const { error } = await supabase
      .from('reviews')
      .delete()
      .eq('id', reviewId);

    if (error) {
      console.error('Error deleting review:', error);
      throw error;
    }
  }

  async getGuides(): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('guides')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching guides:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getGuides:', error);
      throw error;
    }
  }

  async createGuide(guide: {
    title: string;
    summary: string;
    content: string;
    tags?: string[];
    cover_image_url?: string;
  }): Promise<void> {
    try {
      console.log('=== 開始創建指南並翻譯 ===');
      console.log('原始指南資料:', guide);

      // 調用 Supabase Edge Function 進行翻譯
      const { data: translatedData, error: translateError } = await supabase.functions.invoke('translate-guide', {
        body: {
          title: guide.title,
          summary: guide.summary,
          content: guide.content,
          tags: guide.tags || []
        }
      });

      let translation = {};
      if (!translateError && translatedData) {
        translation = translatedData;
        console.log('翻譯成功:', translation);
      } else {
        console.warn('翻譯失敗，將只保存中文版本:', translateError);
      }

      // 準備要插入的資料，包含中英文版本
      const guideData = {
        ...guide,
        title_en: (translation as any).title_en || null,
        summary_en: (translation as any).summary_en || null,
        content_en: (translation as any).content_en || null,
        tags_en: (translation as any).tags_en || null,
      };

      console.log('準備插入資料庫的資料:', guideData);

      const { error } = await supabase
        .from('guides')
        .insert(guideData);

      if (error) {
        console.error('Error creating guide:', error);
        throw error;
      }

      console.log('✅ 指南創建成功（含翻譯）');
    } catch (error) {
      console.error('Error in createGuide:', error);
      throw error;
    }
  }

  async updateGuide(guideId: string, updates: any): Promise<void> {
    try {
      console.log('=== 開始更新指南並翻譯 ===');
      console.log('指南ID:', guideId);
      console.log('更新資料:', updates);

      // 如果有中文內容更新，則進行翻譯
      let translation = {};
      if (updates.title || updates.summary || updates.content || updates.tags) {
        const { data: translatedData, error: translateError } = await supabase.functions.invoke('translate-guide', {
          body: {
            title: updates.title,
            summary: updates.summary,
            content: updates.content,
            tags: updates.tags || []
          }
        });

        if (!translateError && translatedData) {
          translation = translatedData;
          console.log('翻譯成功:', translation);
        } else {
          console.warn('翻譯失敗，將只更新中文版本:', translateError);
        }
      }

      // 準備更新資料，包含翻譯後的英文版本
      const updateData = {
        title: updates.title,
        summary: updates.summary,
        content: updates.content,
        tags: updates.tags,
        cover_image_url: updates.cover_image_url,
        title_en: (translation as any).title_en || updates.title_en,
        summary_en: (translation as any).summary_en || updates.summary_en,
        content_en: (translation as any).content_en || updates.content_en,
        tags_en: (translation as any).tags_en || updates.tags_en,
        updated_at: new Date().toISOString(),
      };

      console.log('準備更新到資料庫的資料:', updateData);

      const { error } = await supabase
        .from('guides')
        .update(updateData)
        .eq('id', guideId);

      if (error) {
        console.error('Error updating guide:', error);
        throw error;
      }

      console.log('✅ 指南更新成功（含翻譯）');
    } catch (error) {
      console.error('Error in updateGuide:', error);
      throw error;
    }
  }

  async deleteGuide(guideId: string): Promise<void> {
    try {
      console.log('=== AdminService.deleteGuide 開始 ===');
      console.log('要刪除的指南ID:', guideId);
      
      // 檢查管理員權限
      const { data: currentUser } = await supabase.auth.getUser();
      console.log('當前用戶ID:', currentUser.user?.id);
      
      if (currentUser.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', currentUser.user.id)
          .single();
        
        console.log('用戶權限資料:', profile);
        
        if (!profile?.is_admin) {
          console.error('權限不足：用戶不是管理員');
          throw new Error('權限不足：您沒有權限執行此操作');
        }
        
        console.log('✅ 管理員權限驗證通過');
      }

      // 先檢查指南是否存在
      const { data: existingGuide, error: selectError } = await supabase
        .from('guides')
        .select('*')
        .eq('id', guideId)
        .single();

      if (selectError) {
        console.error('查詢指南時發生錯誤:', selectError);
        throw selectError;
      }

      if (!existingGuide) {
        console.log('指南不存在，ID:', guideId);
        throw new Error('指南不存在');
      }

      console.log('找到要刪除的指南:', existingGuide.title);

      // 執行刪除
      const { error: deleteError } = await supabase
        .from('guides')
        .delete()
        .eq('id', guideId);

      if (deleteError) {
        console.error('刪除指南時發生錯誤:', deleteError);
        throw deleteError;
      }

      console.log('✅ 指南刪除成功');

      // 驗證刪除結果
      const { data: verifyData, error: verifyError } = await supabase
        .from('guides')
        .select('id')
        .eq('id', guideId);

      if (verifyError) {
        console.warn('驗證刪除時發生錯誤:', verifyError);
      } else if (verifyData && verifyData.length > 0) {
        console.error('🚨 警告：指南仍然存在於資料庫中！');
        throw new Error('刪除失敗：指南仍然存在於資料庫中');
      } else {
        console.log('✅ 驗證成功：指南已從資料庫中移除');
      }

    } catch (error) {
      console.error('AdminService.deleteGuide 發生錯誤:', error);
      throw error;
    }
  }

  async getArticles(): Promise<Article[]> {
    // First get articles
    const { data: articles, error: articlesError } = await supabase
      .from('articles')
      .select('*')
      .order('created_at', { ascending: false });

    if (articlesError) {
      console.error('Error fetching articles:', articlesError);
      throw articlesError;
    }

    if (!articles) return [];

    // Then get profiles for the authors
    const authorIds = articles.map(article => article.author_id);
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, nickname')
      .in('id', authorIds);

    if (profilesError) {
      console.error('Error fetching profiles:', profilesError);
      // Don't throw error, just continue without profile data
    }

    // Combine the data
    const articlesWithProfiles: Article[] = articles.map(article => ({
      ...article,
      profiles: profiles?.find(profile => profile.id === article.author_id) || null
    }));

    return articlesWithProfiles;
  }

  async createArticle(article: Omit<Article, 'id' | 'created_at' | 'updated_at' | 'author_id' | 'profiles'>): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase
      .from('articles')
      .insert({
        ...article,
        author_id: user.id,
      });

    if (error) {
      console.error('Error creating article:', error);
      throw error;
    }
  }

  async updateArticle(articleId: string, updates: Partial<Article>): Promise<void> {
    const { error } = await supabase
      .from('articles')
      .update(updates)
      .eq('id', articleId);

    if (error) {
      console.error('Error updating article:', error);
      throw error;
    }
  }

  async deleteArticle(articleId: string): Promise<void> {
    const { error } = await supabase
      .from('articles')
      .delete()
      .eq('id', articleId);

    if (error) {
      console.error('Error deleting article:', error);
      throw error;
    }
  }

  async getSystemSettings(): Promise<SystemSetting[]> {
    const { data, error } = await supabase
      .from('system_settings')
      .select('*')
      .order('setting_key');

    if (error) {
      console.error('Error fetching system settings:', error);
      throw error;
    }

    return data || [];
  }

  async updateSystemSetting(key: string, value: string): Promise<void> {
    const { error } = await supabase
      .from('system_settings')
      .upsert({
        setting_key: key,
        setting_value: value,
      });

    if (error) {
      console.error('Error updating system setting:', error);
      throw error;
    }
  }
}

export const adminService = new AdminService();
